import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useBooleanFlagValue } from "@openfeature/react-sdk";
import { Flex, Space, Spin } from "antd";
import { createStyles } from "antd-style";

import { ATS_PUBLIC_APP_PATH } from "@av/apps/recruitment/ats-common/routes/jobs/jobs.route-config";
import { QUERY_PARAMS } from "@av/apps/recruitment/ats-common/routes/jobs/query-params";
import {
  hasError,
  mapErrors,
  mapIndexedListServerErrors,
} from "@av/apps/recruitment/ats-common/services/api-config/errors/extract-validation-errors";
import { App } from "@av/components/atoms/app/App";
import { Button } from "@av/components/atoms/Button";
import { Form } from "@av/components/atoms/form/Form";
import { Typography } from "@av/components/atoms/typography/Typography";
import { isSupportedLocale } from "@av/components/organisms/editor/localized-text-editor/text-editor-supported-locales";
import { ConsentCard } from "./components/ConsentCard";
import { DocsUploadCard } from "./components/document-upload/DocsUploadCard";
import { GeneralInformationCard } from "./components/GeneralInformationCard";
import { JobApplicationBanner } from "./components/JobApplicationBanner";
import { ApplicationSubmitted } from "./components/JobApplicationSubmitted";
import { QuestionsCard } from "./components/QuestionsCard";
import { PublicNotFoundPage } from "../../../components/errors/PublicNotFoundPage";
import { usePublicApplyToJob } from "../../../services/apply/apply";
import { usePublicFetchSingleJobPost } from "../../../services/public-job-posts/public-job-posts";
import { JobNotAvailable } from "../components/JobNotAvailable";

import type { CandidateDataUI } from "../../../services/apply/types";

const useStyles = createStyles(({ css, token }) => ({
  page: css`
    width: 100%;
    min-height: 100vh;
  `,
  headerRow: css`
    margin-bottom: ${token.marginLG}px;
  `,
  backLink: css`
    display: inline-flex;
    align-items: center;
    gap: ${token.marginXS}px;
    color: ${token.colorPrimaryText};
    cursor: pointer;
  `,
  backIcon: css`
    color: ${token.colorPrimary};
  `,
  form: css`
    background: transparent;
    padding: 0;
  `,
  bannerSpacing: css`
    margin-bottom: ${token.marginLG}px;
  `,
  footerActions: css`
    margin-bottom: ${token.marginXL * 2}px;
  `,
}));

export function JobApplicationPage() {
  const { t } = useTranslation();
  const { companyId, jobPostId } = useParams();
  const [searchParams] = useSearchParams();
  const locale = searchParams.get(QUERY_PARAMS.locale);
  const localeSearch =
    locale && isSupportedLocale(locale) ? `?${QUERY_PARAMS.locale}=${locale}` : "";
  const { data: jobPost, isLoading: isLoadingJobPost } = usePublicFetchSingleJobPost(
    companyId,
    jobPostId
  );
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { styles } = useStyles();
  const { Title } = Typography;
  const { mutate: applyToJob, isPending: isApplying, error } = usePublicApplyToJob();
  const isConsentEnabled = useBooleanFlagValue("ats_candidate_consent", false);

  const isSubmitInProgressRef = useRef(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  if (!jobPostId || !companyId || (!jobPost && !isLoadingJobPost)) {
    return <PublicNotFoundPage />;
  }

  if (
    jobPostId &&
    jobPost &&
    !isLoadingJobPost &&
    (jobPost?.postStatus === "ARCHIVED" || jobPost?.postStatus === "CLOSED")
  ) {
    return <JobNotAvailable jobPostId={jobPostId} />;
  }

  if (!jobPost && isLoadingJobPost) {
    return <Spin size="large" />;
  }

  const handleSubmit = (values: CandidateDataUI) => {
    if (isSubmitInProgressRef.current) return;

    isSubmitInProgressRef.current = true;

    applyToJob(
      { ...values, jobPostId },
      {
        onSuccess: () => {
          message.success(t("recruitment.jobApplication.submitSuccess"));
          setIsSubmitted(true);
        },
        onError: (axiosError) => {
          isSubmitInProgressRef.current = false;

          if (hasError(axiosError, "JobPostNoLongerAvailable")) {
            message.error(t("recruitment.jobApplication.jobPostNoLongerAvailableError"));
            navigate({
              pathname: `/${ATS_PUBLIC_APP_PATH}/${companyId}/${jobPostId}/view`,
              search: localeSearch,
            });

            return;
          }

          if (hasError(axiosError, "IncompleteAnswers")) {
            message.error(t("recruitment.jobApplication.screeningQuestionsChangedError"));

            return;
          }

          message.error(t("recruitment.jobApplication.submitError"));
        },
      }
    );
  };

  const handleSubmitFailed = () => {
    message.error(t("recruitment.jobApplication.submitError"));
  };

  if (isSubmitted) {
    return <ApplicationSubmitted />;
  }

  return (
    <div className={styles.page}>
      <Flex
        justify="space-between"
        className={styles.headerRow}
        data-testid="job-application-header"
      >
        <Title
          level={5}
          className={styles.backLink}
          onClick={() =>
            navigate({
              pathname: `/${ATS_PUBLIC_APP_PATH}/${companyId}`,
              search: localeSearch,
            })
          }
          role="button"
          tabIndex={0}
          testIdPrefix="job-application-title"
        >
          <ArrowLeftOutlined size={11} className={styles.backIcon} />
          {t("recruitment.jobApplication.backToJobs", "All jobs")}
        </Title>
        <Space>
          <Button
            type="primary"
            htmlType="submit"
            form="candidate-application-form"
            loading={isApplying}
            disabled={isApplying}
            testIdPrefix="submit-application"
          >
            {t("recruitment.jobApplication.submit")}
          </Button>
        </Space>
      </Flex>

      <div className={styles.bannerSpacing}>
        <JobApplicationBanner companyId={companyId} jobPostId={jobPostId} />
      </div>

      <div>
        <Form<CandidateDataUI>
          id="candidate-application-form"
          className={styles.form}
          onFinish={handleSubmit}
          onFinishFailed={handleSubmitFailed}
          serverErrors={mapIndexedListServerErrors(
            mapErrors(t, error),
            "screeningAnswers",
            "actualBooleanAnswer"
          )}
          requiredMark="optional"
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 8 }}
        >
          <Flex vertical>
            <DocsUploadCard />
            <GeneralInformationCard />
            {jobPost?.screeningQuestions && (
              <QuestionsCard questions={jobPost.screeningQuestions} />
            )}
            {isConsentEnabled && <ConsentCard />}
          </Flex>
        </Form>
      </div>

      <Flex
        justify="flex-end"
        className={styles.footerActions}
        data-testid="job-application-footer"
      >
        <Space>
          <Button
            type="primary"
            htmlType="submit"
            form="candidate-application-form"
            loading={isApplying}
            disabled={isApplying}
            testIdPrefix="submit-application"
          >
            {t("recruitment.jobApplication.submit")}
          </Button>
        </Space>
      </Flex>
    </div>
  );
}
